import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { OpenHomeTime } from '@classifieds-sell/datalayer';
import { Subject } from 'rxjs';
import { getHourAndMinuteFromNumber } from '../../../helpers/number.helper';
import { isNullOrWhiteSpace } from '@classifieds-sell/shared';
import { DraftFacade } from '@classifieds-sell/state';
import { PropertySectionBase } from '../../property-section-base';
import { ListingDurationService } from '../../../services/listing-duration.service';

@Component({
    selector: 'property-open-home-times-section',
    templateUrl: './property-open-home-times-section.component.html',
    styleUrls: ['./property-open-home-times-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyOpenHomeTimesSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('propertyOpenHomeTimesSectionForm')
    propertyOpenHomeTimesSectionForm: NgForm;

    minDate = new Date();
    maxDate = new Date();

    minTime = 5;
    maxTime = 23;
    maxOpenHomeTimes = 8;

    openHomeDate: Date | null = null;
    openHomeStartTime = 0;
    openHomeEndTime = 0;

    hasError = false;
    errorMessage = '';
    allowToAdd = false;
    exceedMax = false;

    // HACK: for UI/X requirement
    openHomeDateHasError = false;
    openHomeStartTimeHasError = false;
    openHomeEndTimeHasError = false;

    openHomeTimes: OpenHomeTime[] = [];

    invalidOpenHomeTimeErrorMessage =
        'Please select open home times within your listing duration';
    overlappedOpenHomeTimeErrorMessage =
        'You can’t add an overlapping open home time';
    invalidStartTimeErrorMessage = 'Start time must be earlier than end time';
    openHomeTimeMustBeInFutureErrorMessage = 'Open home end time must be in the future';
    cannotAddOpenHomeWithoutAnAddressErrorMessage =
        'You can’t add open home times without a full address';

    constructor(
        public draftFacade: DraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _listingDurationService: ListingDurationService
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {

            this.maxDate = this._listingDurationService.availableDateText(this.metadata, this.draftData.propertyInformation.duration);

            this.openHomeTimes =
                this.draftData.propertyInformation.openHomeTimes || [];

            if (
                !isNullOrWhiteSpace(
                    this.draftData.propertyInformation.streetNumber
                ) &&
                !isNullOrWhiteSpace(
                    this.draftData.propertyInformation.streetName
                )
            ) {
                this.allowToAdd = true;
                if (this.openHomeStartTime === 0){
                  this.openHomeStartTime = 12;
                }
                if (this.openHomeEndTime === 0){
                  this.openHomeEndTime = 12.5;
                }
            } else {
                this.allowToAdd = false;
            }

            this.exceedMax = this.openHomeTimes.length >= this.maxOpenHomeTimes;

            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        this.setDefault();
        return !this.hasInvalidItem();
    }

    dirty(): boolean {
        return this.propertyOpenHomeTimesSectionForm.form.dirty;
    }

    onDateChange(newDate: Date): void {
        this.openHomeDate = newDate;
        this.openHomeDateHasError = false;
    }

    onOpenStartTimeChange(): void {
        this.openHomeStartTimeHasError = false;
        if(this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeEndTime?.untouched){
          if(this.openHomeStartTime >= 5 && this.openHomeStartTime <= 22.5){
            this.openHomeEndTime = this.openHomeStartTime + 0.5;
          } else if (this.openHomeStartTime === 22.75){
            this.openHomeEndTime = 23;
          }
        }

    }

    onOpenStartEndChange(): void {
        this.openHomeEndTimeHasError = false;
    }

    setDefault(): void {
        this.hasError = false;
        this.openHomeDateHasError = false;
        this.openHomeStartTimeHasError = false;
        this.openHomeEndTimeHasError = false;
        this.openHomeDate = null;
        this.openHomeStartTime = 12;
        this.openHomeEndTime = 12.5;
        this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeDate?.markAsUntouched();
        this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeStartTime?.markAsUntouched();
        this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeEndTime?.markAsUntouched();
        this._changeDetectorRef.markForCheck();
    }

    onAddOpenHomeTimeClick(): void {
        this.hasError = false;

        if (!this.isValidOpenHomeTime()) {
            this.hasError = true;
            this.openHomeDateHasError = true;
            this.openHomeStartTimeHasError = true;
            this.openHomeEndTimeHasError = true;
            return;
        }

        this.openHomeTimes.push({
            date: this.openHomeDate,
            startTime: this.openHomeStartTime,
            endTime: this.openHomeEndTime
        });

        this.updateState();
        this.setDefault();
    }

    isValidItem(index: number): boolean {
        if (!this.allowToAdd) {
            return false;
        }

        const listedDate = new Date(this.openHomeTimes[index].date);

        if (
            this.isPastDateTime(listedDate, this.openHomeTimes[index].endTime)
        ) {
            return false;
        }

        if (listedDate > this.maxDate) {
            return false;
        }
        return true;
    }

    hasInvalidItem(): boolean {
        if (!this.allowToAdd && this.openHomeTimes.length > 0) {
            return true;
        }
        return this.openHomeTimes.some((_, i) => !this.isValidItem(i));
    }

    removeItem(index: number): void {
        if (index > -1) {
            this.openHomeTimes.splice(index, 1);
            this.updateState();
        }
        this.propertyOpenHomeTimesSectionForm?.form.markAsDirty();
    }

    getErrorMessages(): string[] {
        const errors: string[] = [];
        if (
            this.openHomeTimes.some((o) =>
                this.isPastDateTime(new Date(o.date), o.endTime)
            )
        ) {
            errors.push(this.openHomeTimeMustBeInFutureErrorMessage);
        }

        if (this.openHomeTimes.some((o) => new Date(o.date) > this.maxDate)) {
            errors.push(this.invalidOpenHomeTimeErrorMessage);
        }

        return errors;
    }

    private updateState(): void {
        this.draftData.propertyInformation.openHomeTimes = [
            ...this.openHomeTimes.sort((a, b) =>
                new Date(a.date) > new Date(b.date)
                    ? 1
                    : new Date(a.date).toISOString() ===
                      new Date(b.date).toISOString()
                    ? a.startTime > b.startTime
                        ? 1
                        : -1
                    : -1
            )
        ];
        this.publish();
    }

    private isValidOpenHomeTime(): boolean {
        this.propertyOpenHomeTimesSectionForm?.form?.markAllAsTouched();

        let isValidRequired = true;

        if (this.openHomeDate === null) {
            this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeDate.updateValueAndValidity();
            isValidRequired = false;
        }

        if (this.openHomeStartTime === 0) {
            this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeStartTime.setErrors(
                { required: true }
            );
            isValidRequired = false;
        }

        if (this.openHomeEndTime === 0) {
            this.propertyOpenHomeTimesSectionForm?.form?.controls?.openHomeEndTime.setErrors(
                { required: true }
            );
            isValidRequired = false;
        }

        if (!isValidRequired) {
            return false;
        }

        if (this.openHomeDate > this.maxDate) {
            this.errorMessage = this.invalidOpenHomeTimeErrorMessage;
            return false;
        }

        if (this.openHomeStartTime >= this.openHomeEndTime) {
            this.errorMessage = this.invalidStartTimeErrorMessage;
            return false;
        }

        if (this.isPastDateTime(this.openHomeDate, this.openHomeEndTime)) {
            this.errorMessage = this.openHomeTimeMustBeInFutureErrorMessage;
            return false;
        }

        if (
            this.openHomeTimes.some(
                (s) =>
                    new Date(s.date).toISOString() ===
                    this.openHomeDate.toISOString()
            )
        ) {
            if (
                this.openHomeTimes.some(
                    (s) =>
                        s.startTime <= this.openHomeStartTime &&
                        this.openHomeStartTime < s.endTime &&
                        new Date(s.date).toISOString() ===
                            this.openHomeDate.toISOString()
                ) ||
                this.openHomeTimes.some(
                    (s) =>
                        s.startTime < this.openHomeEndTime &&
                        this.openHomeEndTime <= s.endTime &&
                        new Date(s.date).toISOString() ===
                            this.openHomeDate.toISOString()
                )
            ) {
                this.errorMessage = this.overlappedOpenHomeTimeErrorMessage;
                return false;
            }
        }

        this.propertyOpenHomeTimesSectionForm?.form.markAsDirty();
        this.errorMessage = '';
        return true;
    }

    private isPastDateTime(date: Date, time: number): boolean {
        const hourAndMinute = getHourAndMinuteFromNumber(time);
        const openHomeDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            hourAndMinute[0],
            hourAndMinute[1]
        );

        return openHomeDate <= new Date();
    }
}
